// React
import React, { useEffect, useState, useRef } from "react";

// MUI
import {
  Grid,
  Typography,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  Collapse,
  Autocomplete,
  Paper,
  Tooltip,
  CircularProgress,
} from "@mui/material";

//MUI ICON
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoIcon from "@mui/icons-material/Info";
import ReactDOM from "react-dom";
import SaveIcon from "assets/images/SaveIcon.svg";
// Formik
import { useFormik } from "formik";
import * as Yup from "yup";

//Components
import Dropzone from "components/Dropzone_one";
import CustomDropzone from "components/Dropzone";
import {
  GradientButton,
  GradientOutline,
  GradientButtonOutline,
  GradientShadow,
} from "components/GradientButton";
import Form from "components/Form/FormBuyItems";

// API
import api from "_api";

// Other
import { Divider } from "antd";
import Swal from "sweetalert2";
import { set } from "lodash";
import { PaymentChannelOptions, PrefixOptions } from "contants/options";
import RenderFieldsCustomerPawn from "components/Pawn/RenderFieldsCustomer";
import AlertToast from "components/AlertToast";

const WatermarkedImage = ({ src, width = 200, height = 200, alt = "" }) => {
  const canvasRef = useRef(null);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (!src || !canvasRef.current || hasError) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    // ล้าง Canvas ก่อนวาดใหม่
    ctx.clearRect(0, 0, width, height);

    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = src;

    img.onload = () => {
      // 1. วาดรูปต้นฉบับ
      ctx.drawImage(img, 0, 0, width, height);

      // 2. ตั้งค่าลายน้ำ
      ctx.font = `bold ${Math.max(width / 15, 12)}px Arial`;
      ctx.fillStyle = "rgba(255, 0, 0, 0.3)";
      ctx.textAlign = "center";

      // 3. วาดลายน้ำแนวทแยง
      const step = Math.max(width, height) / 2;
      for (let i = -height; i < width * 2; i += step) {
        ctx.save();
        ctx.translate(i, i);
        ctx.rotate((-45 * Math.PI) / 180);
        ctx.fillText("FOR INTERNAL USE", 0, 0);
        ctx.restore();
      }
    };

    img.onerror = () => {
      setHasError(true);
      // วาดพื้นหลังเมื่อโหลดรูปไม่สำเร็จ
      ctx.fillStyle = "#f0f0f0";
      ctx.fillRect(0, 0, width, height);
      ctx.fillStyle = "red";
      ctx.textAlign = "center";
      ctx.fillText("Image Failed to Load", width / 2, height / 2);
    };
  }, [src, width, height, hasError]);

  // แสดงผลลัพธ์
  return (
    <div style={{ position: "relative", width, height }}>
      {hasError ? (
        <div
          style={{
            width: "100%",
            height: "100%",
            background: "#f0f0f0",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "red",
            border: "1px dashed #ccc",
          }}
        >
          Failed to Load Image
        </div>
      ) : (
        <canvas
          ref={canvasRef}
          width={width}
          height={height}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
          }}
          aria-label={alt}
        />
      )}
    </div>
  );
};

// ฟังก์ชันสำหรับสร้าง validation schema ให้กับฟอร์ม
const generateValidationSchema = (productBoxes) => {
  // Dynamic validation fields from productBoxes
  const validationFields = productBoxes.reduce((schema, box) => {
    schema[`product_name_${box.id}`] =
      Yup.string().required(`โปรดระบุชื่อสินค้า`);
    schema[`category_id_${box.id}`] = Yup.string().required(
      `โปรดระบุหมวดหมู่สินค้า`
    );
    schema[`total_amount_${box.id}`] = Yup.number().required(`โปรดระบุจำนวน`);
    schema[`price_${box.id}`] = Yup.number().required(`โปรดระบุราคา`);
    // schema[`total_price_${box.id}`] =
    //   Yup.number().required(`โปรดระบุจำนวนทั้งหมด`);
     schema[`images_${box.id}`] = Yup.array()
    .min(1, "โปรดอัปโหลดรูปภาพอย่างน้อย 1 รูป")
    .required("โปรดอัปโหลดรูปภาพอย่างน้อย 1 รูป");
    return schema;
  }, {});

  // Static validation fields
  const staticFields = {
    prefix: Yup.string().required("โปรดระบุคำนำหน้า"),
    fullName: Yup.string().required("โปรดระบุชื่อ-สกุล"),
    idCardNumber: Yup.string().when("nationality", {
      is: (value) => value === "ไทย",
      then: (schema) =>
        schema
          .matches(/^\d+$/, "รหัสบัตรประชาชนต้องเป็นตัวเลขเท่านั้น")
          .length(13, "รหัสบัตรประชาชนต้องมี 13 หลัก")
          .required("โปรดระบุรหัสบัตรประชาชน"),
      otherwise: (schema) =>
        schema
          .matches(
            /^[A-Za-z0-9]+$/,
            "เลขหนังสือเดินทางต้องเป็นตัวอักษรหรือตัวเลขเท่านั้น"
          )
          .min(6, "เลขหนังสือเดินทางต้องมีอย่างน้อย 6 หลัก")
          .max(20, "เลขหนังสือเดินทางกรอกได้มากสุด 20 หลัก")
          .required("โปรดระบุเลขหนังสือเดินทาง"),
    }),
    phoneNumber: Yup.string()
      .min(9, "หมายเลขโทรศัพท์ต้องมีอย่างน้อย 9 หลัก")
      .max(10, "หมายเลขโทรศัพท์กรอกได้มากสุด 10 หลัก")
      .matches(/^\d+$/, "หมายเลขโทรศัพท์ต้องเป็นตัวเลขเท่านั้น")
      .required("โปรดระบุหมายเลขโทรศัพท์"),
    address: Yup.string().required("โปรดระบุที่อยู่"),
  };

  // Merge dynamic and static fields
  return Yup.object({
    ...validationFields, // Dynamic fields
    ...staticFields, // Static fields
  });
};

// กำนหดตัวแปรสำหรับการดึงข่้อมูลไปแสดงผลใน Field หมวดหมู่
const categoryOptions = [
  { label: "เครื่องประดับ", id: 1 },
  { label: "อิเล็กทรอนิกส์", id: 2 },
  { label: "ทองคำ", id: 3 },
  { label: "พระเครื่อง", id: 4 },
  { label: "แบรนด์เนม", id: 5 },
  { label: "นาฬิกา", id: 6 },
];

const ModalCreate = ({ open, onClose, selectedProduct, fetchProductData }) => {
  console.log("open > ", open);

  console.log("selectedProduct", selectedProduct);
  // ตัวแปรสำหรับเก็บข้อมูลรูปภาพ
  const [files1, setFiles1] = useState([]);
  const [files2, setFiles2] = useState([]);
  const [error1, setError1] = useState(null);
  const [error2, setError2] = useState(null);
  const [initialProductUrls, setInitialProductUrls] = useState([]);
  const [deletedImageIds, setDeletedImageIds] = useState([]);

  console.log("files2 > ", files2);
  

  // ตัวแปรสำหรับเก็บข้อของ Box แสดง Product
  const [productBoxes, setProductBoxes] = useState([
    { id: 1, isExpanded: false },
  ]);
  const [expandedRow, setExpandedRow] = useState(null); // เก็บการแสดงผลของ Row ในการ Explaned
  const [statusSave, setStatusSave] = useState(false); // เก็บ status หลังจากมีการเพิ่ม Box
  const [currentStep, setCurrentStep] = useState(1); // Track the current step
  // const [selectedOption, setSelectedOption] = useState(1); // For selecting the display count option
  const [isHovered, setIsHovered] = useState(false);
  const [activeButton, setActiveButton] = useState("pawnContract");
  const [isModalClosing, setIsModalClosing] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);

  //เก็บตัวแปร id หลังจากสร้าง product เสร็จ
  const [newIds, setNewIds] = useState([]);
  const [order, setOrder] = useState(null);

  const [loading, setLoading] = useState(false);
  // สถานะปุ่มบันทึกข้อมูล
  const [isSuccess, setIsSuccess] = useState(false);
  const [toast, setToast] = useState({
    open: false,
    message: "",
    type: "success",
  });

  const updateProductBox = (id, isExpanded) => {
    setProductBoxes((prev) => {
      const exists = prev.some((box) => box.id === id);

      if (exists) {
        // อัปเดตออบเจกต์ที่มีอยู่
        return prev.map((box) =>
          box.id === id ? { ...box, isExpanded } : box
        );
      } else {
        // เพิ่มออบเจกต์ใหม่
        return [...prev, { id, isExpanded }];
      }
    });
  };

  //ฟังก์ชันแสดง Explanded
  const handleExpandClick = (rowId) => {
    console.log("expandedRow444", rowId);

    setExpandedRow(expandedRow === rowId ? null : rowId);
  };

  const [expandedBoxes, setExpandedBoxes] = useState();

  //ฟังก์ชันลบ Box
  const handleDeleteBox = async (rowId) => {
    console.log("rowId to delete", rowId);
    if (productBoxes.length > 1) {
      let product_id = formik.values[`product_id_${rowId}`];
      console.log("product_id to delete", product_id);
      if (product_id) {
        try {
          const response = await api.product.deleteProduct(product_id);
          console.log("response delete product", response);
          if (response) {
            console.log("Deleted product successfully");

            // ลบ Box ที่เลือก
            setProductBoxes((prevBoxes) =>
              prevBoxes.filter((box) => box.id !== rowId)
            );
            setNewIds((prevIds) => prevIds.filter((id) => id !== product_id));
            //ลบออกจาก formik ด้วย
            const updatedValues = { ...formik.values };
            delete updatedValues[`product_id_${rowId}`];
            delete updatedValues[`product_code_${rowId}`];
            delete updatedValues[`product_code_com_${rowId}`];
            delete updatedValues[`product_name_${rowId}`];
            delete updatedValues[`category_id_${rowId}`];
            delete updatedValues[`detail_${rowId}`];
            delete updatedValues[`blemishes_${rowId}`];
            delete updatedValues[`size_${rowId}`];
            delete updatedValues[`total_amount_${rowId}`];
            delete updatedValues[`price_${rowId}`];
            delete updatedValues[`total_price_${rowId}`];
            delete updatedValues[`notes_${rowId}`];
            delete updatedValues[`images_${rowId}`];
            formik.setValues(updatedValues);
          } else {
            console.error("Error deleting product");
          }
        } catch (error) {
          console.error("Error deleting product:", error);
        }
      } else {
        // ลบ Box ที่เลือก
        setProductBoxes((prevBoxes) =>
          prevBoxes.filter((box) => box.id !== rowId)
        );
      }
    } else {
      alert("Cannot delete the last remaining box.");
    }
  };

  //ฟังก์ชันเปลี่ยนหน้า
  const handleNextStep = async () => {
    setCurrentStep(currentStep + 1); // Go to the next "page"
  };

  //ฟังก์ชันสำหรับการเปลี่ยนหน้าแสดงผลใน Modal
  const handleBackToStep1 = () => {
    setCurrentStep(1); // Reset back to the first step if needed
  };

  // ฟังก์ชันเพิ่ม product box
  const addProductBox = () => {
    if (productBoxes.length < 15) {
      setProductBoxes([
        ...productBoxes,
        { id: productBoxes.length + 1, isExpanded: false },
      ]);
      setStatusSave(false);
    } else {
      alert("คุณสามารถเพิ่มสินค้าได้สูงสุด 15 รายการเท่านั้น");
    }
  };

  // ฟังก์ชัน explan
  const toggleExpand = (boxId) => {
    console.log("boxId", boxId);
    if (selectedProduct) {
      setExpandedBoxes((prevId) => (prevId === boxId ? null : boxId));
    } else {
      setProductBoxes(
        productBoxes.map((box) =>
          box.id === boxId ? { ...box, isExpanded: !box.isExpanded } : box
        )
      );
    }
  };

  // สร้างรหัสตัวแปรตามจำนวน Box ที่เพิ่ม
  const generateInitialValues = (productBoxes) =>
    productBoxes.reduce(
      (values, { id }) => ({
        ...values,
        payment_channel: "",
        [`product_id_${id}`]: "",
        [`product_code_${id}`]: "",
        [`product_code_com_${id}`]: "",
        [`product_name_${id}`]: "",
        [`category_id_${id}`]: "",
        [`detail_${id}`]: "",
        [`blemishes_${id}`]: "",
        [`size_${id}`]: "",
        [`total_amount_${id}`]: "",
        [`price_${id}`]: "",
        [`total_price_${id}`]: "",
        [`notes_${id}`]: "",
        [`images_${id}`]: [],
      }),
      {}
    );

  // นับจำนวน Box
  const productBoxCount = productBoxes.length;

  // console.log("display new id in array", productBoxes);

  // Function to convert object to FormData
  const transformObjectToFormData = (obj) => {
    const formData = new FormData();

    Object.entries(obj).forEach(([key, value]) => {
      if (key === "category_id" && value?.id) {
        // Append category id if it's an object with id
        formData.append(key, value.id);
      } else if (Array.isArray(value)) {
        // Handle arrays like image uploads
        value.forEach((item, index) => {
          if (item instanceof File) {
            // Append files in FormData
            formData.append(`${key}[${index}]`, item);
          } else {
            // Append other array items
            formData.append(`${key}[${index}]`, item);
          }
        });
      } else {
        // Append other values directly
        formData.append(key, value);
      }
    });

    return formData;
  };

  // Formik hook
  const formik = useFormik({
    initialValues: {
      prefix: "",
      fullName: "",
      idCardNumber: "",
      phoneNumber: "",
      address: "",
      nationality: "ไทย",
      ...generateInitialValues(productBoxes),
    },
    validationSchema: generateValidationSchema(productBoxes),
    onSubmit: async (values, { resetForm }) => {
      console.log("values of customer", values);
      console.log("files2", files2);
      try {
        setLoading(true);
        const customerData = {
          prefix: values.prefix,
          fullName: values.fullName,
          idCardNumber: values.idCardNumber,
          phoneNumber: values.phoneNumber,
          address: values.address,
          nationality: values.nationality || "ไทย",
          file: files2.map((fileObj) => fileObj.file),
        };

        // console.log("newIds445", newIds);

        const customerFormData = transformObjectToFormData(customerData);
        const customerResponse = await api.product.createCustomer(
          customerFormData
        );

        if (customerResponse.isStatus) {
          // If customer creation is successful, proceed to create orders for each product box
          const orderDataArray = productBoxes.map((box) => {
            return {
              product: newIds,
              customer: {
                id: customerResponse.data.id,
              },
              payment_channel: values.payment_channel,
            };
          });

          // Loop through the orderDataArray to create orders for each product
          if (orderDataArray.length > 0) {
            const orderResponse = await api.product.createOrder(
              orderDataArray[0]
            );
            // console.log({ orderResponse });

            // console.log("Order response for first item:", orderResponse);

            if (!orderResponse.isStatus) {
              console.error("Error creating order for first item.");
            }

            if (orderResponse.isStatus) {
              setOrder(orderResponse.data);
              setTimeout(() => {
                setLoading(false);
                setIsSuccess(true);
              }, 1000);
            }
          }

          // // If all orders were created successfully, reset form, close modal, and fetch data
          // setFiles1([]); // Clear product images
          // setFiles2([]); // Clear customer images
          // resetForm();
          // onClose();
          // fetchProductData();
          // setCurrentStep(1);
          // setProductBoxes([{ id: 1, isExpanded: false }]);
          // selectedProduct = productBoxes;
          // setNewIds([]);
        } else {
          console.error("Error creating customer.");
          throw new Error("Error creating customer.");
        }
      } catch (error) {
        setLoading(false);
        console.error("Error submitting form:", error);
        setToast({
          open: true,
          message: `เกิดข้อผิดพลาดในการบันทึกข้อมูล`,
          type: "error",
        });
        // alert(`Error submitting form: ${error.message}`);
      } finally {
        setLoading(false);
      }
    },
    validateOnChange: true,
    validateOnBlur: true,
  });

  const handlePrintPDF = async () => {
    if (order) {
      await api.product
        .getGenerateAndPrintPdfBuyProduct(order?.createdOrder)
        .then((res) => {
          // console.log("res", res);
          const url = window.URL.createObjectURL(
            new Blob([res.data], { type: "application/pdf" })
          );
          window.open(url, "_blank");
        })
        .catch((err) => {
          console.log("err", err);
        });
    }
  };

  const handleClose = () => {
    fetchProductData();
    formik.resetForm();
    setFiles1([]); // Clear product images
    setFiles2([]); // Clear customer images

    setCurrentStep(1);
    setProductBoxes([{ id: 1, isExpanded: false }]);

    setNewIds([]);
    setLoading(false);
    setIsSuccess(false);
    onClose();
  };

  // ฟังก์ชันลบรูป
  const handleImageDelete = (boxId, imageIndex) => {
    if (selectedProduct) {
      const currentOrderItem = selectedProduct.order_items.find(
        (item) => item.id === boxId
      );
      if (imageIndex < currentOrderItem.pictures.length)
        setDeletedImageIds([
          ...deletedImageIds,
          currentOrderItem.pictures[imageIndex].id,
        ]);
    }
    const currentImages = formik.values[`images_${boxId}`] || [];
    // Safeguard against undefined or null images array
    const updatedImages = currentImages.filter(
      (_, index) => index !== imageIndex
    );

    formik.setFieldValue(`images_${boxId}`, updatedImages);
  };

  useEffect(() => {
    if (selectedProduct) {
      // console.log("selectedProduct", selectedProduct);
      updateProductBox(selectedProduct?.order_items[0].id, true);
      // ตรวจสอบว่า order_items มีข้อมูล และเป็นอาร์เรย์
      if (
        selectedProduct?.order_items &&
        Array.isArray(selectedProduct.order_items)
      ) {
        const formikValues = {};
        const productUrls = {};

        selectedProduct.order_items.forEach((pawn) => {
          console.log("check pawn", pawn.category_id);
          const id = pawn.id; // ใช้ `id` แยกฟิลด์แต่ละ box
          const category_id = pawn.category_id;
          const category_order = categoryOptions.find(
            (category) => category.id === category_id
          );

          // เพิ่มฟิลด์สำหรับแต่ละ box
          formikValues[`product_id_${id}`] = pawn.product_id || "";
          formikValues[`product_code_${id}`] = pawn.product_code || "";
          formikValues[`product_code_com_${id}`] = pawn.product_code_com || "";
          formikValues[`product_name_${id}`] = pawn.product_name || "";
          formikValues[`category_id_${id}`] = category_order?.id || null;
          formikValues[`detail_${id}`] = pawn.detail || "";
          formikValues[`blemishes_${id}`] = pawn.blemishes || "";
          formikValues[`notes_${id}`] = pawn.notes || "";
          formikValues[`size_${id}`] = pawn.size || "";
          formikValues[`total_amount_${id}`] = pawn.total_amount || 1;
          formikValues[`price_${id}`] = pawn.product_price || 0;
          formikValues[`total_price_${id}`] = pawn.total_price || 0;

          // เก็บรูปภาพในฟิลด์ `images_${id}`
          const images = Array.isArray(pawn.pictures)
            ? pawn.pictures.flatMap((picObj) =>
                Array.isArray(picObj.picture) ? picObj.picture : []
              )
            : [];
          console.log("images", images);
          formikValues[`images_${id}`] = images;

          // Check if images exist before setting them to initialProductUrls
          if (images.length > 0) {
            setInitialProductUrls((prevUrls) => ({
              ...prevUrls,
              [`images_${id}`]: images,
            }));
          }
        });

        // เพิ่มข้อมูลลูกค้า
        formikValues.prefix = selectedProduct.prefix || "";
        formikValues.fullName = selectedProduct.customer_name || "";
        formikValues.idCardNumber = selectedProduct.idcard || "";
        formikValues.phoneNumber = selectedProduct.phonenumber || "";
        formikValues.address = selectedProduct.address || "";
        formikValues.nationality = selectedProduct.nationality || "ไทย";
        formikValues.product_images = selectedProduct.picture || [];
        formikValues.late_fees = selectedProduct.late_fees || 0;
        formikValues.payment_channel = selectedProduct.payment_channel || "";

        // ตั้งค่า Formik values
        formik.setValues(formikValues);
      }
    }
  }, [selectedProduct]);

  // ฟังก์ชันการแสดงฟิลด์ Customer
  const renderFields = (fields) => {
    console.log("rields", fields);
    return fields.map((field, index) => (
      <React.Fragment key={index}>
        <Grid item xs={12} sm={12} sx={{ margin: 2 }}>
          <Box display="flex" alignItems="center">
            <Typography
              variant="subtitle1"
              sx={{ width: "200px", whiteSpace: "nowrap" }}
            >
              {field.label}
              {field.required && (
                <span style={{ marginLeft: "4px", color: "red" }}>*</span>
              )}
            </Typography>

            {field.type === "autocomplete" ? (
              <Autocomplete
                id={field.name}
                options={field.options || []}
                value={formik.values.prefix}
                onChange={(event, newValue) => {
                  if (!field.disabled) {
                    formik.setFieldValue("prefix", newValue);
                  }
                }}
                disabled={field.disabled}
                fullWidth
                sx={{
                  flexGrow: 1,
                  ".MuiAutocomplete-inputRoot": {
                    height: "40px",
                    padding: "0px !important", // Ensure consistent padding
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    name={field.name}
                    error={
                      formik.touched[field.name] &&
                      Boolean(formik.errors[field.name])
                    }
                    helperText={
                      formik.touched[field.name] && formik.errors[field.name]
                    }
                    fullWidth
                    InputProps={{
                      ...params.InputProps,
                      sx: { height: "40px", padding: "0px" }, // Ensure consistent height
                    }}
                  />
                )}
              />
            ) : (
              <TextField
                disabled={field.disabled || selectedProduct}
                id={field.name}
                name={field.name}
                type={field.type}
                value={formik.values[field.name]}
                onChange={formik.handleChange}
                {...getFieldProps(field.name)}
                error={
                  formik.touched[field.name] &&
                  Boolean(formik.errors[field.name])
                }
                helperText={
                  formik.touched[field.name] && formik.errors[field.name]
                }
                fullWidth
                InputProps={{
                  sx: { height: "40px", padding: "0px" }, // Ensure consistent height and padding
                  inputProps: {
                    style: {
                      "-moz-appearance": "textfield",
                      "-webkit-appearance": "none",
                      appearance: "none",
                    },
                    maxLength:
                      field.name === "idCardNumber"
                        ? 13
                        : field.name === "phoneNumber"
                        ? 10
                        : undefined,
                    inputMode: field.type === "text" ? "numeric" : undefined,
                    pattern: field.type === "text" ? "[0-9]*" : undefined,
                  },
                }}
              />
            )}
          </Box>
        </Grid>
      </React.Fragment>
    ));
  };

  const checkValidationError = (boxId, fieldName) => {
    return (
      formik.touched[`${fieldName}_${boxId}`] &&
      Boolean(formik.errors[`${fieldName}_${boxId}`])
    );
  };
  // ฟังก์ชันการแสดงฟิลด์ของ Product

  const renderProductBoxes = () => {
    const boxesToRender = selectedProduct
      ? selectedProduct.order_items.map((item) => ({
          id: item.id,
          isExpanded: expandedBoxes === item.id, // เปิดเฉพาะกล่องที่ตรงกับ expandedBoxes
          ...item,
        }))
      : productBoxes;
    return boxesToRender.map((box, index) => (
      <Box
        key={box.id}
        sx={{
          my: 2,
          border: "1px solid #ddd",
          p: 2,
          borderRadius: "12px",
          backgroundColor: "#ffffff",
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center" // Add this to center items vertically
          onClick={() => toggleExpand(box.id)}
        >
          <Typography variant="subtitle1">สินค้าชิ้นที่ {index + 1}</Typography>
          <div>
            {!selectedProduct && (
              <IconButton
                disabled={productBoxes.length > 1 ? false : true}
                size="small"
                color="#ccc"
                onClick={(e) => {
                  e.stopPropagation(); // Prevent toggleExpand from triggering on delete click
                  handleDeleteBox(box.id); // Call the delete function
                }}
              >
                <DeleteIcon />
              </IconButton>
            )}
            <IconButton
              size="small"
              color="#ccc"
              onClick={() => handleExpandClick(box.id)}
              style={{
                transform: expandedBoxes === box.id ? "rotate(180deg)" : "none",
                transition: "transform 0.2s ease-in-out",
                marginLeft: "10px",
              }}
            >
              <ExpandMoreIcon />
            </IconButton>
          </div>
        </Box>
        <Collapse in={box.isExpanded}>
          <Grid container spacing={2} style={{ marginTop: "10px" }}>
            <Divider sx={{ borderColor: "#00BB9F", borderWidth: 1 }} />
            {/* Product Code2 Row */}
            <Grid item xs={12}>
              <Grid container alignItems="center">
                <Grid item xs={2}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography>รหัส 2 {""}</Typography>
                    <Tooltip title="รหัสสินค้าภายในร้านที่สามารถกำหนดเองได้">
                      <InfoIcon style={{ color: "#e5e5e5", marginRight: 10 }} />
                    </Tooltip>
                  </div>
                </Grid>

                <Grid item xs={10}>
                  <TextField
                    fullWidth
                    name={`product_code_com_${box.id}`}
                    value={formik.values[`product_code_com_${box.id}`] || ""}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur} // <-- Add onBlur
                    error={
                      formik.touched[`product_code_com_${box.id}`] &&
                      Boolean(formik.errors[`product_code_com_${box.id}`])
                    }
                    helperText={
                      formik.touched[`product_code_com_${box.id}`] &&
                      formik.errors[`product_code_com_${box.id}`]
                    }
                    InputProps={{
                      sx: { height: "40px", padding: "0px" }, // Ensure consistent height
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Product Name Row */}
            <Grid item xs={12}>
              <Grid container alignItems="center">
                <Grid item xs={2}>
                  <Typography>
                    ชื่อสินค้า{""}
                    {!selectedProduct ? (
                      <span style={{ marginLeft: "4px", color: "red" }}>*</span>
                    ) : null}
                  </Typography>
                </Grid>
                <Grid item xs={10}>
                  <TextField
                    // disabled={selectedProduct ? true : false}
                    fullWidth
                    name={`product_name_${box.id}`}
                    value={formik.values[`product_name_${box.id}`] || ""}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur} // <-- Add onBlur
                    error={
                      formik.touched[`product_name_${box.id}`] &&
                      Boolean(formik.errors[`product_name_${box.id}`])
                    }
                    helperText={
                      formik.touched[`product_name_${box.id}`] &&
                      formik.errors[`product_name_${box.id}`]
                    }
                    InputProps={{
                      sx: { height: "40px", padding: "0px" }, // Ensure consistent height
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Product Category Row */}
            <Grid item xs={12}>
              <Grid container alignItems="center">
                <Grid item xs={2}>
                  <Typography>
                    หมวดหมู่{""}
                    {!selectedProduct ? (
                      <span style={{ marginLeft: "4px", color: "red" }}>*</span>
                    ) : null}
                  </Typography>
                </Grid>
                <Grid item xs={10}>
                  <Autocomplete
                    // disabled={selectedProduct ? true : false}
                    id={`category_id_${box.id}`}
                    options={categoryOptions}
                    getOptionLabel={(option) => option.label || ""} // Ensure label is retrieved correctly
                    value={
                      categoryOptions.find(
                        (option) =>
                          option.id === formik.values[`category_id_${box.id}`]
                      ) || null
                      // formik.values[`category_id_${box.id}`]?.id
                    }
                    error={
                      formik.touched[`category_id_${box.id}`] &&
                      formik.errors[`category_id_${box.id}`]
                    }
                    helperText={
                      formik.touched[`category_id_${box.id}`] &&
                      formik.errors[`category_id_${box.id}`]
                    }
                    onChange={(event, newValue) => {
                      formik.setFieldValue(
                        `category_id_${box.id}`,
                        newValue?.id || "" // Store only the id in Formik's state
                      );
                    }}
                    fullWidth
                    sx={{
                      ".MuiAutocomplete-inputRoot": {
                        height: "40px", // Set consistent height
                        padding: "0 8px", // Use slight padding to keep input structure intact
                      },
                      ".MuiOutlinedInput-root": {
                        height: "40px", // Ensure consistent height in outer wrapper
                      },
                      ".MuiAutocomplete-input": {
                        padding: "0px", // Minimal padding for input text
                      },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        name={`category_id_${box.id}`} // Use correct name for validation
                        error={
                          formik.touched[`category_id_${box.id}`] &&
                          Boolean(formik.errors[`category_id_${box.id}`])
                        }
                        helperText={
                          formik.touched[`category_id_${box.id}`] &&
                          formik.errors[`category_id_${box.id}`]
                        }
                        fullWidth
                        InputProps={{
                          ...params.InputProps,
                          sx: { height: "40px", padding: "0px" }, // Ensure consistent height
                        }}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Product Detail Row */}
            <Grid item xs={12}>
              <Grid container alignItems="center">
                <Grid item xs={2}>
                  <Typography>รายละเอียด</Typography>
                </Grid>
                <Grid item xs={10}>
                  <TextField
                    // disabled={selectedProduct ? true : false}
                    fullWidth
                    name={`detail_${box.id}`}
                    value={formik.values[`detail_${box.id}`] || ""}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur} // <-- Add onBlur
                    error={
                      formik.touched[`detail_${box.id}`] &&
                      Boolean(formik.errors[`detail_${box.id}`])
                    }
                    helperText={
                      formik.touched[`detail_${box.id}`] &&
                      formik.errors[`detail_${box.id}`]
                    }
                    InputProps={{
                      sx: { height: "40px", padding: "0px" }, // Ensure consistent height
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Product Blemishes Row */}
            <Grid item xs={12}>
              <Grid container alignItems="center">
                <Grid item xs={2}>
                  <Typography>ตำหนิ</Typography>
                </Grid>
                <Grid item xs={10}>
                  <TextField
                    // disabled={selectedProduct ? true : false}
                    fullWidth
                    name={`blemishes_${box.id}`}
                    value={formik.values[`blemishes_${box.id}`] || ""}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur} // <-- Add onBlur
                    error={
                      formik.touched[`blemishes_${box.id}`] &&
                      Boolean(formik.errors[`blemishes_${box.id}`])
                    }
                    helperText={
                      formik.touched[`blemishes_${box.id}`] &&
                      formik.errors[`blemishes_${box.id}`]
                    }
                    InputProps={{
                      sx: { height: "40px", padding: "0px" }, // Ensure consistent height
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Product Size Row */}
            <Grid item xs={12}>
              <Grid container alignItems="center">
                <Grid item xs={2}>
                  <Typography>ขนาด</Typography>
                </Grid>
                <Grid item xs={10}>
                  <TextField
                    // disabled={selectedProduct ? true : false}
                    fullWidth
                    name={`size_${box.id}`}
                    value={formik.values[`size_${box.id}`] || ""}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur} // <-- Add onBlur
                    error={
                      formik.touched[`size_${box.id}`] &&
                      Boolean(formik.errors[`size_${box.id}`])
                    }
                    helperText={
                      formik.touched[`size_${box.id}`] &&
                      formik.errors[`size_${box.id}`]
                    }
                    InputProps={{
                      sx: { height: "40px", padding: "0px" }, // Ensure consistent height
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Product Total amount Row */}
            <Grid item xs={12}>
              <Grid container alignItems="center">
                <Grid item xs={2}>
                  <Typography>
                    จำนวน{" "}
                    {!selectedProduct ? (
                      <span style={{ marginLeft: "4px", color: "red" }}>*</span>
                    ) : null}
                  </Typography>
                </Grid>
                <Grid item xs={10}>
                  <TextField
                    disabled={selectedProduct ? true : false}
                    fullWidth
                    type="number"
                    name={`total_amount_${box.id}`}
                    value={formik.values[`total_amount_${box.id}`] || ""}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched[`total_amount_${box.id}`] &&
                      Boolean(formik.errors[`total_amount_${box.id}`])
                    }
                    helperText={
                      formik.touched[`total_amount_${box.id}`] &&
                      formik.errors[`total_amount_${box.id}`]
                    }
                    InputProps={{
                      min: 1,
                      sx: { height: "40px", padding: "0px" }, // Ensure consistent height
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Product Price Row */}
            <Grid item xs={12}>
              <Grid container alignItems="center">
                <Grid item xs={2}>
                  <Typography>
                    ราคาทุน{""}
                    {!selectedProduct ? (
                      <span style={{ marginLeft: "4px", color: "red" }}>*</span>
                    ) : null}
                  </Typography>
                </Grid>
                <Grid item xs={10}>
                  <TextField
                    disabled={selectedProduct ? true : false}
                    fullWidth
                    type="number"
                    name={`price_${box.id}`}
                    value={formik.values[`price_${box.id}`] || ""}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched[`price_${box.id}`] &&
                      Boolean(formik.errors[`price_${box.id}`])
                    }
                    helperText={
                      formik.touched[`price_${box.id}`] &&
                      formik.errors[`price_${box.id}`]
                    }
                    InputProps={{
                      sx: { height: "40px", padding: "0px" }, // Ensure consistent height
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Product Total Price Row */}
            <Grid item xs={12}>
              <Grid container alignItems="center">
                <Grid item xs={2}>
                  <Typography>
                    ราคารวม{""}
                    {!selectedProduct ? (
                      <span style={{ marginLeft: "4px", color: "red" }}>*</span>
                    ) : null}
                  </Typography>
                </Grid>
                <Grid item xs={10}>
                  <TextField
                    fullWidth
                    type="number"
                    name={`total_price_${box.id}`}
                    value={
                      formik.values[`total_amount_${box.id}`] &&
                      formik.values[`price_${box.id}`]
                        ? Number(formik.values[`total_amount_${box.id}`]) *
                          Number(formik.values[`price_${box.id}`])
                        : ""
                    }
                    // No onChange because total_price is calculated dynamically
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched[`total_price_${box.id}`] &&
                      Boolean(formik.errors[`total_price_${box.id}`])
                    }
                    helperText={
                      formik.touched[`total_price_${box.id}`] &&
                      formik.errors[`total_price_${box.id}`]
                    }
                    InputProps={{
                      sx: { height: "40px", padding: "0px" }, // Ensure consistent height
                    }}
                    disabled // Disable input since it's calculated
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* <Grid item xs={12}>
              <Grid container alignItems="center">
                <Grid item xs={2}>
                  <Typography>
                  ช่องทางการชำระ{""}
                    {!selectedProduct ? (
                      <span style={{ marginLeft: "4px", color: "red" }}>*</span>
                    ) : null}
                  </Typography>
                </Grid>
                <Grid item xs={10}>
                <Autocomplete
                    fullWidth
                    id="autocomplete-payment-method"
                    options={PaymentChannelOptions}
                    getOptionLabel={(option) => option.label}
                    value={
                      PaymentChannelOptions?.find(
                        (opt) => opt?.id ===formik.values[`payment_method_${box.id}`] 
                      ) || null
                    }
                    onChange={(event, newValue) => {
                      formik.setFieldValue(
                        `payment_method_${box.id}`,
                        newValue?.id ?? ""
                      );
                    }}
                    sx={{
                      ".MuiAutocomplete-inputRoot": {
                        height: "40px",
                        padding: "0px 0px 0px 7px !important",
                      },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        name={`payment_method_${box.id}`}
                        variant="outlined"
                        placeholder="เลือกช่องทางการชำระ"
                        fullWidth
                        InputProps={{
                          ...params.InputProps,
                          sx: { height: "40px" }, // Ensure consistent height
                        }}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Grid> */}
            {/* Product Notes Row */}
            <Grid item xs={12}>
              <Grid container alignItems="center">
                <Grid item xs={2}>
                  <Typography>หมายเหตุ</Typography>
                </Grid>
                <Grid item xs={10}>
                  <TextField
                    fullWidth
                    name={`notes_${box.id}`}
                    value={formik.values[`notes_${box.id}`] || ""}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur} // <-- Add onBlur
                    error={
                      formik.touched[`notes_${box.id}`] &&
                      Boolean(formik.errors[`notes_${box.id}`])
                    }
                    helperText={
                      formik.touched[`notes_${box.id}`] &&
                      formik.errors[`notes_${box.id}`]
                    }
                    InputProps={{
                      sx: { height: "40px", padding: "0px" }, // Ensure consistent height
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Product Image Row */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                รูปทรัพย์
              </Typography>
              <Grid item xs={12}>
                {/* {selectedProduct ? (
                  <div>
                    {Array.isArray(formik.values[`images_${box.id}`]) &&
                    formik.values[`images_${box.id}`].length > 0 ? (
                      <div>
                        {formik.values[`images_${box.id}`].map(
                          (imageUrl, index) => (
                            <img
                              key={index}
                              src={imageUrl}
                              alt={`Image ${index + 1} for Box ${box.id}`}
                              style={{
                                width: "150px",
                                height: "150px",
                                objectFit: "cover",
                                marginRight: "10px",
                              }}
                            />
                          )
                        )}
                      </div>
                    ) : (
                      <p>No images available for Box {box.id}</p>
                    )}
                  </div>
                ) : ( */}
                <>
                  <CustomDropzone
                    files={
                      Array.isArray(formik.values[`images_${box.id}`])
                        ? formik.values[`images_${box.id}`]
                        : formik.values[`images_${box.id}`]
                        ? [formik.values[`images_${box.id}`]] // ถ้ามีค่าแต่ไม่ใช่ array → แปลงเป็น array
                        : []
                    }
                    // setFiles={(newFiles) =>
                    //   formik.setFieldValue(`images_${box.id}`, newFiles)
                    // }
                    setFiles={(newFiles) => {
                      formik.setFieldValue(`images_${box.id}`, newFiles);
                      formik.setFieldTouched(`images_${box.id}`, true);
                    }}
                    // initialUrls={initialProductUrls[`images_${box.id}`] || []}
                    setError={setError1}
                    onDelete={(index) => handleImageDelete(box.id, index)}
                    isModalClosing={isModalClosing}
                  />
                  {error1 && (
                    <Typography variant="caption" color="error">
                      {error1}
                    </Typography>
                  )}
                </>
                {/* )} */}
              </Grid>
            </Grid>
          </Grid>

          <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
            <Button
              variant="outlined"
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              onClick={async () => {
                // formik.handleSubmit();
                // check validation ก่อนบันทึก

                try {
                  let product_id = formik.values[`product_id_${box.id}`];

                  // Existing images from formik state
                  const existingImages =
                    formik.values[`images_${box.id}`] || [];                    

                  // Separate new files (without id) from existing images (with id)
                  const newFiles = existingImages
                    .filter((img) => !img.id)
                    .map((fileObj) => fileObj.file);
                  const combinedFiles = newFiles.filter(Boolean); // New files to be uploaded

                  // ตรวจภาพก่อนทุกเคส
                  const imageErrors = formik.errors[`images_${box.id}`];
                  if (!existingImages.length) {
                    formik.setFieldTouched(`images_${box.id}`, true); 
                    setError1(imageErrors || "โปรดอัปโหลดรูปภาพอย่างน้อย 1 รูป");
                    return;
                  } else {
                    setError1(""); // เคลียร์ error ถ้ามีรูปแล้ว
                  }

                  if (
                    formik.values[`product_name_${box.id}`] &&
                    formik.values[`category_id_${box.id}`] &&
                    formik.values[`total_amount_${box.id}`] &&
                    formik.values[`price_${box.id}`]
                  ) {
                    // Collect product details
                    const boxValues = {
                      product_code: formik.values[`product_code_${box.id}`],
                      product_code_com:
                        formik.values[`product_code_com_${box.id}`],
                      product_name: formik.values[`product_name_${box.id}`],
                      category_id: formik.values[`category_id_${box.id}`],
                      detail: formik.values[`detail_${box.id}`],
                      blemishes: formik.values[`blemishes_${box.id}`],
                      size: formik.values[`size_${box.id}`],
                      total_amount: formik.values[`total_amount_${box.id}`],
                      price: formik.values[`price_${box.id}`],
                      total_price: formik.values[`total_price_${box.id}`],
                      // payment_method: formik.values[`payment_method_${box.id}`],
                      notes: formik.values[`notes_${box.id}`],
                      files: combinedFiles,
                    };

                    const formData = transformObjectToFormData(boxValues);
                    
                    if (!product_id) {                      
                      // Create new product
                      const productResponse = await api.product.createProduct(
                        formData
                      );
                      console.log("createProduct", productResponse);

                      if (productResponse && productResponse.isStatus) {
                        setToast({
                          open: true,
                          type: "success",
                          message: "บันทึกข้อมูลสำเร็จ",
                        });
                        const { createdProduct } = productResponse?.data;
                        const newProductId =
                          productResponse.data.createdProduct.product_id;
                        const newProductCode =
                          productResponse.data.createdProduct.product_code;
                        setStatusSave(true);

                        // Set product_id in Formik state
                        await formik.setFieldValue(
                          `product_id_${box.id}`,
                          newProductId
                        );
                        await formik.setFieldValue(
                          `product_code_${box.id}`,
                          newProductCode
                        );

                        //เดี๋ยวมาแก้ตรงนี้นะ
                        // setNewIds([...newIds, newProductId, newProductCode]);
                        setNewIds([...newIds, newProductId]);

                        // Extract and set the pictures (ensure they are formatted properly)
                        const pictures =
                          createdProduct?.Pictures?.map((pic, index) => ({
                            id: pic.id,
                            url: pic.pic, // Server returns URL
                            name: pic.pic.split("/").pop() || "Image Complete", // Retain file name
                            size: 0, // Retain file size
                            // status: "complete", // Indicate upload status
                          })) || [];

                        // Update Formik state with the returned images
                        await formik.setFieldValue(
                          `images_${box.id}`,
                          pictures
                        );
                      }
                    } else {                      
                      // Existing product update
                      const updateValues = {
                        product_id: product_id,
                        product_code: formik.values[`product_code_${box.id}`],
                        product_code_com:
                          formik.values[`product_code_com_${box.id}`],
                        product_name: formik.values[`product_name_${box.id}`],
                        category_id: formik.values[`category_id_${box.id}`],
                        detail: formik.values[`detail_${box.id}`],
                        blemishes: formik.values[`blemishes_${box.id}`],
                        size: formik.values[`size_${box.id}`],
                        total_amount: formik.values[`total_amount_${box.id}`],
                        price: formik.values[`price_${box.id}`],
                        total_price: formik.values[`total_price_${box.id}`],
                        payment_method:
                          formik.values[`payment_method_${box.id}`],
                        notes: formik.values[`notes_${box.id}`],
                        deleteFiles:
                          deletedImageIds?.length > 0 ? deletedImageIds : [], // Handle deleted images
                        files: combinedFiles,
                      };

                      const formDataUpdate =
                        transformObjectToFormData(updateValues);
                      const updateProductResponse =
                        await api.product.updateProduct(formDataUpdate);

                      console.log("updateProduct", updateValues);

                      if (updateProductResponse.isStatus) {
                        const { data } = updateProductResponse;

                        const updatedPictures = Array.isArray(data?.pictures)
                          ? data?.pictures?.map((pic) => ({
                              ...pic,
                              url: pic.pic,
                              name:
                                pic.pic.split("/").pop() || "Image Complete",
                            }))
                          : [];
                        console.log("updatedPictures", updatedPictures);

                        await formik.setFieldValue(
                          `images_${box.id}`,
                          updatedPictures
                        );

                        setToast({
                          open: true,
                          type: "success",
                          message: "อัปเดตข้อมูลสำเร็จ",
                        });
                      } else {
                        console.error(
                          "Failed to update the product.",
                          updateProductResponse
                        );
                      }
                    }
                  } else {
                    const errors = await formik.validateForm();
                    if (Object.keys(errors).length > 0) {
                      formik.setTouched(
                        Object.keys(errors).reduce((touchedObj, key) => {
                          touchedObj[key] = true;
                          return touchedObj;
                        }, {})
                      );
                      return; // Prevent moving to the next step if there are validation errors
                    }
                  }
                } catch (error) {
                  console.error(`Error saving product ${box.id}:`, error);
                  setToast({
                    open: true,
                    type: "error",
                    message: "เกิดข้อผิดพลาดในการบันทึกข้อมูล",
                  });
                }
              }}
              style={{
                height: "50px",
                width: "200px",
                color: "#000",
                borderColor: "#ddd",
                borderRadius: "12px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Typography style={{ marginRight: "8px" }}>
                บันทึกข้อมูล
              </Typography>
              {isHovered && <CheckCircleIcon color="success" />}
            </Button>
          </Box>
        </Collapse>
      </Box>
    ));
  };

  const renderFieldsPayment = () => {
    return (
      <Box
        sx={{
          my: 2,
          border: "1px solid #ddd",
          p: 2,
          borderRadius: "12px",
          backgroundColor: "#ffffff",
        }}
      >
        <Grid container spacing={2} style={{ marginTop: "10px" }}>
          {/* Product Payment Channel Row */}
          <Grid item xs={12}>
            <Grid container alignItems="center">
              <Grid item xs={2}>
                <Typography>
                  ช่องทางการซื้อ{""}
                  <span style={{ marginLeft: "4px", color: "red" }}>*</span>
                </Typography>
              </Grid>
              <Grid item xs={10}>
                <Autocomplete
                  id={`payment_channel`}
                  options={PaymentChannelOptions}
                  getOptionLabel={(option) => option.label}
                  value={
                    PaymentChannelOptions.find(
                      (opt) => opt.id === formik.values.payment_channel
                    ) || null
                  }
                  disabled={selectedProduct ? true : false}
                  onChange={(event, newValue) => {
                    formik.setFieldValue(
                      `payment_channel`,
                      newValue ? newValue.id : ""
                    );
                  }}
                  sx={{
                    ".MuiAutocomplete-inputRoot": {
                      height: "40px",
                      padding: "0px 0px 0px 5px !important",
                    },
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name={`payment_channel`}
                      error={
                        formik.touched.payment_channel &&
                        Boolean(formik.errors.payment_channel)
                      }
                      helperText={
                        formik.touched.payment_channel &&
                        formik.errors.payment_channel
                      }
                      fullWidth
                      InputProps={{
                        ...params.InputProps,
                        sx: { height: "40px" },
                      }}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    );
  };
  // console.log("display formik.values", formik.values);
  // กรณีปิดหน้าต่าง Modal
  const handleCancel = () => {
    // const formHasUnsavedChanges = Object.keys(touched).some(
    //   (field) => touched[field]
    // );

    const formHasUnsavedChanges =
      Object.keys(touched).some((field) => touched[field]) || files2.length > 0;
    console.log("aaa", formHasUnsavedChanges);
    // console.log('formHasUnsavedChanges', formHasUnsavedChanges);
    // Prompt the user to confirm before closing if there are unsaved changes
    if (formHasUnsavedChanges) {
      Swal.fire({
        html: `
          <div style="display: flex; align-items: center; justify-content: space-between;">
            <!-- Left side icon/picture -->
            <div style="margin-right: 20px;">
              <img src="${SaveIcon}" alt="warning icon" width="300" height="300"/>
            </div>

            <!-- Right side text and buttons -->
            <div style="flex: 1;">
              <h2 style="font-size: 24px; color: #333;">ยืนยันการยกเลิกหรือไม่!</h2>
              <div id="custom-buttons" style="display: flex; justify-content: flex-end; margin-top: 20px;">
                <!-- React buttons will be injected here -->
              </div>
            </div>
          </div>
        `,
        showCancelButton: false, // Disable default buttons
        showConfirmButton: false, // Disable default buttons
        customClass: {
          container: "swal2-container-custom",
        },
        didOpen: () => {
          document.querySelector(".swal2-container-custom").style.zIndex = 1500;

          // Render your React button components after the Swal modal opens
          ReactDOM.render(
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: 1,
                width: "100%",
              }}
            >
              <GradientButton
                onClick={() => {
                  // Handle cancel behavior
                  Swal.close(); // Close Swal
                }}
                style={{ width: "100%", marginBottom: "10px" }} // Make full width
              >
                ยกเลิก
              </GradientButton>
              <GradientButtonOutline
                onClick={() => {
                  // Close the current Swal first before showing the confirmation Swal
                  formik.resetForm();
                  Object.keys(formik.values).forEach((key) => {
                    if (key.startsWith("images_"))
                      formik.setFieldValue(key, []);
                  });

                  setFiles1([]); // Clear product images
                  setFiles2([]); // Clear customer images
                  setInitialProductUrls([]); // Clear initial product image URLs
                  // setInitialCustomerUrls([]);
                  setDeletedImageIds([]);

                  setNewIds([]);
                  fetchProductData();
                  setExpandedBoxes(null);
                  setProductBoxes([{ id: 1, isExpanded: false }]);

                  setIsSuccess(false);
                  Swal.close();
                  onClose();
                  // window.location.reload();
                }}
                style={{ width: "100%" }}
              >
                ตกลง
              </GradientButtonOutline>
            </Box>,
            document.getElementById("custom-buttons") // Inject React components into this div
          );
        },
      });
    } else {
      formik.resetForm();
      setFiles1([]); // Clear product images
      setFiles2([]); // Clear customer images
      setInitialProductUrls([]); // Clear initial product image URLs
      // setInitialCustomerUrls([]);
      setDeletedImageIds([]);
      onClose();
      setNewIds([]);
      setExpandedBoxes(null);
      // window.location.reload();
      fetchProductData();
      setIsSuccess(false);
      setProductBoxes([{ id: 1, isExpanded: false }]);
    }
  };

  // Function to handle button click
  const handleButtonClick = (buttonName) => {
    setActiveButton(buttonName); // Set the clicked button as active
  };

  const { errors, touched, getFieldProps, handleSubmit } = formik;

  // ใช้สำหรับการคิดค่าของ ราคา * จำนวน = จำนวนทั้งหมดแล้วนำไปเก็บไว้ใน Formik id นั้นๆ
  useEffect(() => {
    productBoxes.forEach((box) => {
      const totalAmount = formik.values[`total_amount_${box.id}`];
      const price = formik.values[`price_${box.id}`];

      if (totalAmount && price) {
        const totalPrice = Number(totalAmount) * Number(price);
        formik.setFieldValue(`total_price_${box.id}`, totalPrice);
      }
    });
  }, [formik.values, productBoxes]);

  // start websocket
  //let intendPresent_flag = true;
  const [intendPresentFlag, setIntendPresentFlag] = useState(true);

  let wSocket = null;

  const dataURLtoFile = (dataurl, filename) => {
    const arr = dataurl.split(",");
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  };

  const setAutoReadOptions = () => {
    const cmdNIDAutoRead = document.getElementById("chkAutoRead").checked;
    const cmdIDNumber = document.getElementById("chkAutoNIDNumber").checked;
    const cmdText = document.getElementById("chkAutoNIDText").checked;
    const cmdphoto = document.getElementById("chkAutoNIDPhoto").checked;
    const JS_OBJ = {
      Command: "SetAutoReadOptions",
      AutoRead: cmdNIDAutoRead,
      IDNumberRead: cmdIDNumber,
      IDTextRead: cmdText,
      IDATextRead: true,
      IDPhotoRead: true,
    };
    //console.log("JS_OBJ >>> ", JS_OBJ);
    var jsonStr = JSON.stringify(JS_OBJ);
    wSocketSend(jsonStr);
  };

  const wSocketSend = (jsonStr) => {
    // console.log("wSocketSend jsonStr > ", jsonStr);
    wSocket.send(jsonStr);
  };

  const clearScreen = () => {
    console.log("call clearScreen");
    //setTimeout(() => {
    formik.resetForm();
    setFiles2([]);
    //}, 1000);
    //setTimeout(() => {
    // document.getElementById("prefix").value = "";
    // document.getElementById("fullName").value = "";
    // document.getElementById("idCardNumber").value = "";
    // document.getElementById("phoneNumber").value = "";
    // document.getElementById("address").value = "";
    // document.getElementById("nationality").value = "";
    //}, 2000);
    // document.getElementById("phoneNumber").value = "";
  };

  const putToScreen = async (jsObj) => {
    // console.log("jsObj > ", jsObj);
    try {
      let prefix = "";
      let fullName = "";
      let address = "";
      const IDNum = jsObj.ID_Number;
      const IDText = jsObj.ID_Text;
      const IDPhoto = jsObj.ID_Photo;
      const status = jsObj.Status;
      if (status == 0) {
        if (IDText != "") {
          const NIDData = IDText.split("#");
          const prefix_T = NIDData[1];
          const firstName_T = NIDData[2];
          const lastName_T = NIDData[4];
          prefix = prefix_T;
          fullName = firstName_T + " " + lastName_T;
          address =
            NIDData[9] +
            " " +
            NIDData[10] +
            " " +
            NIDData[11] +
            " " +
            NIDData[12] +
            " " +
            NIDData[13] +
            "\r\n" +
            NIDData[14] +
            " " +
            NIDData[15] +
            " " +
            NIDData[16];
        }
      }
      // 2. อัปเดต Formik values พร้อมกัน
      await Promise.all([
        formik.setFieldValue("prefix", prefix),
        formik.setFieldValue("idCardNumber", IDNum),
        formik.setFieldValue("fullName", fullName),
        formik.setFieldValue("address", address),
      ]);
      // await formik.setValues({
      //   ...formik.values,
      //   idCardNumber: IDNum,
      //   fullName: fullName,
      //   address: address,
      //   isFromCard: true
      // }, false); // false = ไม่ต้อง validate

      // 3. อัปเดต touched หลังจาก values ถูกตั้งค่า
      formik.setTouched({
        ...formik.touched,
        idCardNumber: true,
        fullName: true,
        address: true,
      });

      // 4. ตรวจสอบ validation ใหม่
      setTimeout(() => {
        formik.validateForm();
      }, 50);

      // 5. อัปเดตรูปภาพ (ถ้ามี)
      if (IDPhoto != null) {
        const photoUrl = `data:image/jpeg;base64,${IDPhoto}`;
        const fileName = `id_card_${Date.now()}.jpg`;
        const idCardFile = dataURLtoFile(photoUrl, fileName);
        const nidCardFile = {
          file: idCardFile,
          url: photoUrl,
          name: fileName,
          status: "complete",
          isFromCard: true,
        };
        setFiles2([nidCardFile]);
      }
    } catch (error) {
      console.error("Error in putToScreen:", error);
    }
  };
  /*
  const putToScreen = (jsObj) =>{
    document.getElementById("phoneNumber").value = "";
    const IDNum = jsObj.ID_Number;
    const IDText = jsObj.ID_Text;
    const IDPhoto = jsObj.ID_Photo;
    const status = jsObj.Status;
    document.getElementById("idCardNumber").value = IDNum;
    formik.setFieldValue("idCardNumber", IDNum);
    if(status == 0){
      if (IDText != "") {
        const NIDData = IDText.split("#");
        const firstName_T = NIDData[2];
        const lastName_T = NIDData[4];
        const fullName = firstName_T + ' ' + lastName_T;
        document.getElementById("fullName").value = fullName;
        formik.setFieldValue("fullName", fullName);
        const address = NIDData[9] +
        " " +
        NIDData[10] +
        " " +
        NIDData[11] +
        " " +
        NIDData[12] +
        " " +
        NIDData[13] +
        "\r\n" +
        NIDData[14] +
        " " +
        NIDData[15] +
        " " +
        NIDData[16];

        document.getElementById("address").value = address;
        formik.setFieldValue("address", address);
      }
      // อัปเดต touched state สำหรับฟิลด์ที่ถูกเปลี่ยนแปลง
      formik.setTouched({
        ...formik.touched,
        idCardNumber: true,
        fullName: true,
        address: true
      });
      if (IDPhoto != null) {
        const photoUrl = `data:image/jpeg;base64,${IDPhoto}`;
        const fileName = `id_card_${Date.now()}.jpg`;
        const idCardFile = dataURLtoFile(photoUrl, fileName);
        const nidCardFile = {
          file: idCardFile,
          url: photoUrl,
          name: fileName,
          status: "complete",
          isFromCard: true
        };
        setFiles2([nidCardFile]);
      } else {
        // document.getElementById("Photo").src = "images/PNIDCard.JPG";
      }
      document.getElementById("phoneNumber").value = "";
    } else {
      alert("ERROR Code :" + status);
    }
    document.getElementById("phoneNumber").value = "";
  };
  */

  const onGetMessage = (jsonString) => {
    // console.log("jsonString >> ", jsonString);
    const msgObj = JSON.parse(jsonString);
    if (msgObj.Message == "AgentStatusE") {
      if (msgObj.Status == 1) {
        setAutoReadOptions();
      } else {
        alert("ERROR Code :" + msgObj.Status);
      }
    }

    if (msgObj.Message == "CardStatusChangeE") {
      if (msgObj.Status == -1004) {
        alert("ERROR Code :" + msgObj.Status);
        return;
      } else if (msgObj.Status == 1) {
        clearScreen();
        setIntendPresentFlag(false);
      } else if (msgObj.Status == -16) {
        setIntendPresentFlag(true);
        clearScreen();
      } else if (msgObj.Status == 0) {
        alert("Card Status: Unknown");
        clearScreen();
      } else {
        alert("ERROR Code :" + msgObj.Status);
      }
    }

    if (msgObj.Message == "ReadingProgressE") {
    }

    if (msgObj.Message == "AutoReadIDCardE") {
      putToScreen(msgObj);
    }

    if (msgObj.Message == "AutoSelectReaderE") {
    }
  };
  useEffect(() => {
    if (open) {
      wSocket = new WebSocket("ws://localhost:14820/TDKWAgent");
      wSocket.onopen = () => {
        console.log("WebSocket opened");
        setAutoReadOptions();
      };

      wSocket.onmessage = function (evt) {
        // console.log("RAW >>>", evt.data);
        onGetMessage(evt.data);
      };

      wSocket.onerror = (error) => {
        console.log("WebSocket: error", error);
      };

      return () => {
        wSocket.close();
        console.log("WebSocket closed");
      };
    }
  }, [open]);
  // end websocket

  return (
    <>
      <Dialog
        open={open}
        onClose={(event, reason) => {
          //ป้องกันการกดเบื้องหลังแล้วหลุด
          if (reason !== "backdropClick") onClose();
        }}
        disableEscapeKeyDown
        fullWidth
        maxWidth="lg"
        sx={{
          borderRadius: "8px",
        }}
      >
        {currentStep === 1 ? (
          <>
            {/* หน้าที่ 1 : Form */}
            <DialogTitle style={{ backgroundColor: "#F1F2F7" }}>
              <Typography variant="h4">
                {selectedProduct ? "แก้ไขสินค้า" : "ซื้อสินค้า"}
              </Typography>
            </DialogTitle>
            <DialogContent style={{ backgroundColor: "#F1F2F7" }}>
              <form onSubmit={formik.handleSubmit}>
                {/* Render the product boxes */}
                <input
                  type="checkbox"
                  class="form-check-input mx-3"
                  id="chkAutoRead"
                  onclick="setAutoReadOptions();"
                  checked="checked"
                  style={{ display: "none" }}
                ></input>
                <input
                  type="checkbox"
                  class="form-check-input"
                  id="chkAutoNIDNumber"
                  onclick="setAutoReadOptions();"
                  checked="checked"
                  style={{ display: "none" }}
                ></input>
                <input
                  type="checkbox"
                  class="form-check-input"
                  id="chkAutoNIDText"
                  checked="checked"
                  onclick="setAutoReadOptions();"
                  style={{ display: "none" }}
                ></input>
                <input
                  type="checkbox"
                  class="form-check-input"
                  id="chkAutoNIDPhoto"
                  checked="checked"
                  onclick="setAutoReadOptions();"
                  style={{ display: "none" }}
                ></input>
                <Box
                  sx={{
                    flexGrow: 1,
                    p: 2,
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                  }}
                >
                  <RenderFieldsCustomerPawn
                    formik={formik}
                    setCustomerImage={setFiles2}
                    images={
                      selectedProduct && selectedProduct.customer_image
                        ? selectedProduct.customer_image
                        : files2.length > 0
                        ? files2
                        : []
                    }
                    disabled={selectedProduct ? true : false}
                    isEdit={selectedProduct ? true : false}
                    // disabled={selectedProduct || files2.length > 0}
                  />
                  <Box sx={{ mt: 1 }}>
                    {/* Render the product boxes */}
                    <Typography variant="h5">ข้อมูลสินค้า</Typography>
                    {renderProductBoxes()}
                  </Box>
                  {/* Button to add new product box and display count */}
                  {!selectedProduct ? (
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="flex-end"
                    >
                      <Typography>{productBoxCount} / 15</Typography>
                      {productBoxes.length >= 15 ||
                      !productBoxes.every(
                        (box) => formik.values[`product_id_${box.id}`]
                      ) ? (
                        <GradientOutline
                          onClick={addProductBox}
                          disabled={productBoxes.length >= 15 || !statusSave}
                          style={{ marginLeft: "10px" }}
                        >
                          <Typography style={{ marginRight: "8px" }}>
                            เพิ่มสินค้า
                          </Typography>
                          <AddCircleIcon fontSize="medium" />
                        </GradientOutline>
                      ) : (
                        <GradientButton
                          onClick={addProductBox}
                          style={{ marginLeft: "10px" }}
                        >
                          <Typography style={{ marginRight: "8px" }}>
                            เพิ่มสินค้า
                          </Typography>
                          <AddCircleIcon fontSize="medium" />
                        </GradientButton>
                      )}
                    </Box>
                  ) : null}
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="h5">รายละเอียดการซื้อ</Typography>
                    {renderFieldsPayment()}
                  </Box>
                </Box>
              </form>
            </DialogContent>

            <DialogActions
              style={{
                display: "flex",
                justifyContent: "space-between",
                padding: "0 16px 16px 16px",
                backgroundColor: "#F1F2F7",
              }}
            >
              {/* Dialog Actions */}
              <Grid container sx={{ mx: 3 }}>
                <Grid item xs={12}>
                  <Divider
                    style={{
                      marginTop: "0",
                      marginBottom: "16px",
                    }}
                  />
                </Grid>

                <Grid
                  item
                  xs={12}
                  container
                  justifyContent="space-between" // Apply flex properties to align buttons
                >
                  <Button
                    variant="outlined"
                    onClick={handleCancel}
                    sx={{
                      borderRadius: "12px", // Make the button circular
                      minWidth: "40px", // Adjust the width
                      minHeight: "40px", // Adjust the height
                      padding: 0, // Remove padding to center the icon better
                      borderColor: "#c4c4c4", // Light grey border
                      color: "#6c6c6c", // Light grey icon color
                      display: "flex", // Ensure flexbox is used
                      alignItems: "center", // Center vertically
                      justifyContent: "center", // Center horizontally
                      "&:hover": {
                        borderColor: "#a0a0a0", // Darker grey on hover
                      },
                    }}
                  >
                    <ArrowBackIosIcon
                      fontSize="small"
                      style={{
                        marginLeft: "5px",
                      }}
                    />
                  </Button>
                  {productBoxes.every(
                    (box) => formik.values[`product_id_${box.id}`]
                  ) && !selectedProduct ? (
                    <GradientButtonOutline
                      type="submit"
                      onClick={handleNextStep}
                    >
                      ดูตัวอย่าง
                    </GradientButtonOutline>
                  ) : null}
                  {/* <GradientButtonOutline type="submit" onClick={handleNextStep}>
                  ดูตัวอย่าง
                </GradientButtonOutline> */}
                </Grid>
              </Grid>
            </DialogActions>
          </>
        ) : (
          <>
            {/* Step 2: Confirmation Page */}
            <DialogTitle>
              <Typography variant="h4">ตัวอย่างแบบฟอร์มรายการซื้อ</Typography>
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={2}>
                {/* Left Side: PDF Preview (Scrollable) */}
                <Grid
                  item
                  xs={8}
                  style={{
                    backgroundColor: "#f0f0f0",
                    padding: "20px",
                    height: "650px",
                    overflowY: "scroll",
                    overflowX: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      mt: 2,
                      transform: "scale(0.9)",
                      transformOrigin: "top left", // จุดยึดอยู่มุมบนซ้าย
                    }}
                  >
                    <Form data={formik.values} />
                  </Box>
                </Grid>

                {/* Right Side: The Form or other content */}
                <Grid
                  item
                  xs={4}
                  display={"flex"}
                  flexDirection={"column"}
                  style={{ padding: "20px" }}
                >
                  <GradientShadow
                    isActive={activeButton === "pawnContract"}
                    onClick={() => handleButtonClick("pawnContract")}
                    style={{ margin: "10px" }}
                  >
                    ใบเสร็จรับเงิน
                  </GradientShadow>

                  {/* <GradientShadow
                  isActive={activeButton === "saleRenewal"}
                  onClick={() => handleButtonClick("saleRenewal")}
                  style={{ margin: "10px" }}
                >
                  ใบขายฝาก / ต่ออายุ
                </GradientShadow> */}
                  {/* Content for the right side */}
                </Grid>
              </Grid>
            </DialogContent>

            {/* <Divider style={{ marginTop: "0", marginBottom: "16px", mx: 2 }} /> */}

            <DialogActions
              style={{
                display: "flex",
                justifyContent: "space-between",
                padding: "0 16px 16px 16px",
                // backgroundColor: "#F1F2F7",
              }}
            >
              {/* Dialog Actions */}
              <Grid container sx={{ mx: 3 }}>
                <Grid item xs={12}>
                  <Divider
                    style={{
                      marginTop: "0",
                      marginBottom: "16px",
                    }}
                  />
                </Grid>

                <Grid
                  item
                  xs={12}
                  container
                  justifyContent="space-between" // Apply flex properties to align buttons
                >
                  <GradientButtonOutline
                    // GradientButtonOutlineon
                    onClick={handleBackToStep1}
                  >
                    ยกเลิก
                  </GradientButtonOutline>
                  <Grid>
                    {isSuccess ? (
                      <>
                        <GradientButtonOutline
                          sx={{
                            width: "120px",
                            marginRight: "20px",
                          }}
                          endIcon={
                            <CheckCircleIcon
                              sx={{
                                width: "16px",
                                height: "16px",
                              }}
                            />
                          }
                          id="btn-success"
                          onClick={handleClose}
                        >
                          เสร็จสิ้น
                        </GradientButtonOutline>
                        <GradientButton
                          id="btn-print-pdf"
                          onClick={handlePrintPDF}
                          sx={{ width: "100px" }}
                        >
                          พิมพ์
                        </GradientButton>
                      </>
                    ) : (
                      <>
                        <GradientButton
                          type="submit"
                          onClick={formik.handleSubmit}
                          style={{ marginRight: "20px", width: "150px" }}
                          disabled={loading}
                          endIcon={
                            loading ? (
                              <CircularProgress
                                color="inherit"
                                size={15}
                                thickness={5}
                              />
                            ) : null
                          }
                        >
                          บันทึก
                        </GradientButton>
                        <Tooltip
                          title={
                            <Typography
                              sx={{
                                fontSize: "14px",
                              }}
                            >
                              กรุณากดบันทึกก่อนพิมพ์
                            </Typography>
                          }
                          arrow={true}
                          placement="top"
                          disableHoverListener={isSuccess} // ปิด tooltip ถ้า isSuccess = true
                          disableFocusListener={isSuccess}
                        >
                          <span>
                            <GradientButtonOutline
                              disabled={!isSuccess}
                              sx={{
                                width: "100px",
                                border: "1px solid #ccc",
                              }}
                            >
                              พิมพ์
                            </GradientButtonOutline>
                          </span>
                        </Tooltip>
                      </>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            </DialogActions>
          </>
        )}
        <AlertToast
          open={toast.open}
          type={toast.type}
          message={toast.message}
          time={2000}
          onClose={() => setToast({ ...toast, open: false })}
        />
      </Dialog>
    </>
  );
};

export default ModalCreate;
